import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Pill, Btn } from "@/components/kit";
import { usd } from "@/lib/format";
import { Landmark, RefreshCw, CheckCircle2, Users, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function QuickBooks() {
  const [status, setStatus] = useState(null);
  const [prof, setProf] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/quickbooks/status").then((r) => setStatus(r.data));
    api.get("/quickbooks/profitability").then((r) => setProf(r.data));
  };
  useEffect(() => { load(); }, []);

  const sync = async () => { setSyncing(true); try { await api.post("/quickbooks/sync"); toast.success("Synced to QuickBooks Online"); load(); } finally { setSyncing(false); } };

  return (
    <div>
      <PageHeader title="QuickBooks" subtitle="Two-way sync — no double entry. Contacts, estimates, invoices & job costs stay in lockstep"
        actions={<Btn onClick={sync} disabled={syncing} testid="qbo-sync-btn"><RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sync Now</Btn>} />

      <div className="rounded-xl bg-amberw/10 border-2 border-amberw/30 p-3 text-sm text-amberw flex items-center gap-2 mb-4">
        <AlertCircle className="w-4 h-4" /> QuickBooks Online integration is SIMULATED for this demo (realistic two-way sync behavior; connect real QBO OAuth in production).
      </div>

      {status && (
        <>
          <div className="mb-card p-5 mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-successg/10 flex items-center justify-center"><Landmark className="w-5 h-5 text-successg" /></div>
              <div><div className="font-bold text-navy flex items-center gap-2">{status.realm} <CheckCircle2 className="w-4 h-4 text-successg" /></div><div className="text-xs text-slate-400">Connected · last sync just now</div></div>
            </div>
            <Pill tone="green">Connected</Pill>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <StatCard label="Customers Synced" value={status.customers_synced} icon={Users} tone="navy" />
            <StatCard label="Invoices Synced" value={status.invoices_synced} icon={FileText} tone="green" />
            <StatCard label="Unsynced" value={status.unsynced_count} icon={AlertCircle} tone="amber" />
          </div>
        </>
      )}

      {prof && (
        <div className="mb-card overflow-x-auto mb-scroll">
          <div className="px-5 pt-5"><h3 className="font-display font-bold uppercase text-lg text-navy">Job Profitability — Estimated vs Actual</h3></div>
          <table className="w-full text-sm min-w-[720px] mt-3">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Contract</th><th className="px-4 py-3 text-right">Est. Cost</th><th className="px-4 py-3 text-right">Actual Cost</th><th className="px-4 py-3 text-right">Est GP%</th><th className="px-4 py-3 text-right">Actual GP%</th></tr>
            </thead>
            <tbody>
              {prof.jobs.map((j) => (
                <tr key={j.id} data-testid={`prof-${j.id}`} className={`border-t border-slate-100 ${j.below_target ? "bg-dangerr/5" : ""}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{j.customer}</td>
                  <td className="px-4 py-3"><Pill tone="navy">{j.status?.replace("_", " ")}</Pill></td>
                  <td className="px-4 py-3 text-right font-num">{usd(j.contract_cents)}</td>
                  <td className="px-4 py-3 text-right font-num">{usd(j.estimated_cost_cents)}</td>
                  <td className="px-4 py-3 text-right font-num">{usd(j.actual_cost_cents)}</td>
                  <td className="px-4 py-3 text-right font-num">{j.estimated_gp_pct}%</td>
                  <td className={`px-4 py-3 text-right font-num font-bold ${j.below_target ? "text-dangerr" : "text-successg"}`}>{j.actual_gp_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
